import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authAPI } from '@/lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // We can no longer read the httpOnly auth cookie from JS, so always probe the
  // session via /auth/me on mount.
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    authAPI.me()
      .then(res => { if (active) setUser(res.data); })
      .catch(() => { if (active) setUser(null); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  // Reload the current session after the backend has set the auth cookie
  // (used by Google Sign-In).
  const loadSession = useCallback(async () => {
    try {
      const res = await authAPI.me();
      setUser(res.data);
    } catch {
      setUser(null);
    }
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await authAPI.login({ email, password });
    setUser(res.data.user);
    return res.data;
  }, []);

  const register = useCallback(async (data) => {
    const res = await authAPI.register(data);
    setUser(res.data.user);
    return res.data;
  }, []);

  const logout = useCallback(async () => {
    try { await authAPI.logout(); } catch { /* clear locally regardless */ }
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await authAPI.me();
      setUser(res.data);
    } catch (err) {
      // Only treat an explicit auth rejection as "logged out". Transient
      // failures (429 rate-limit, 5xx, network blips) must NOT nuke the
      // session and bounce the user to /auth.
      const status = err?.response?.status;
      if (status === 401 || status === 403) setUser(null);
    }
  }, []);

  const value = useMemo(() => ({
    user, loading, login, register, logout, loadSession, refreshUser,
    isAdmin: user?.role === 'admin',
  }), [user, loading, login, register, logout, loadSession, refreshUser]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
